import React, { useState } from 'react';
import { User, Bell, Search, ChevronDown, Languages, Menu, X, Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Navigation = ({ activeTab, setActiveTab, activeSubMenu, setActiveSubMenu, activeDashboardSubMenu, setActiveDashboardSubMenu, activeReportSubMenu, setActiveReportSubMenu, userRole = 'user', userName = 'User', onLogout }) => {
  const { t, i18n } = useTranslation();
  const [managementDropdownOpen, setManagementDropdownOpen] = useState(false);
  const [dashboardDropdownOpen, setDashboardDropdownOpen] = useState(false);
  const [reportDropdownOpen, setReportDropdownOpen] = useState(false);
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const tabs = userRole === 'admin' ? [
    { key: 'Dashboard', label: t('navigation.dashboard') },
    { key: 'Management', label: t('navigation.management') },
    { key: 'Report', label: t('navigation.report') }
  ] : [
    { key: 'Dashboard', label: t('navigation.dashboard') },
    { key: 'Report', label: t('navigation.report') }
  ];

  const managementSubMenus = [
    { key: 'Subscriber', label: t('managementMenu.subscriber') },
    { key: 'Security Fee', label: t('managementMenu.securityFee') },
    { key: 'File Processing Monitor', label: t('managementMenu.fileProcessing') },
    { key: 'Authorization', label: t('managementMenu.authorization') },
    { key: 'User Profiles', label: t('managementMenu.userProfiles') },
    { key: 'Audit Trail', label: t('managementMenu.auditTrail') },
    { key: 'Account Status', label: t('managementMenu.accountStatus') }
  ];

  const dashboardSubMenus = [
    { key: 'Subscriber Dashboard', label: t('dashboardMenu.subscriber') },
    { key: 'Security Fee Dashboard', label: t('dashboardMenu.securityFee') },
    { key: 'Telecom Regulatory Dashboard', label: t('dashboardMenu.regulatory') },
    { key: 'Regulatory Balance Dashboard', label: t('dashboardMenu.regulatoryBalance') }
  ];

  const reportSubMenus = [
    { key: 'Subscribers Report', label: t('reportMenu.subscribers') },
    { key: 'Security Fee Report', label: t('reportMenu.securityFee') },
    { key: 'Invoice by Operator', label: t('reportMenu.invoiceByOperator') },
    { key: 'Summary Report', label: t('reportMenu.summary') }
  ];

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('language', lng);
    setLanguageDropdownOpen(false);
  };

  return (
    <nav className="bg-white shadow-lg border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo and Brand */}
          <div className="flex items-center">
            <div className="flex-shrink-0 flex items-center gap-2">
              <div className="bg-blue-600 p-1.5 rounded-lg">
                <Shield className="h-5 w-5 text-white" />
              </div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent hidden sm:block">
                {t('navigation.title')}
              </h1>
              <h1 className="text-xl font-bold text-slate-800 sm:hidden">
                BAS
              </h1>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="hidden md:block">
            <div className="ml-10 flex items-baseline space-x-4">
              {tabs.map((tab) => {
                if (tab.key === 'Dashboard') {
                  return (
                    <div key={tab.key} className="relative">
                      <button
                        onClick={() => {
                          setActiveTab(tab.key);
                          setDashboardDropdownOpen(!dashboardDropdownOpen);
                        }}
                        className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 flex items-center space-x-1 ${activeTab === tab.key
                          ? 'bg-primary-100 text-primary-700 border-b-2 border-primary-500'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                          }`}
                      >
                        <span>{tab.label}</span>
                        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${dashboardDropdownOpen ? 'rotate-180' : ''
                          }`} />
                      </button>
                      {dashboardDropdownOpen && activeTab === 'Dashboard' && (
                        <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                          {dashboardSubMenus.map((subMenu) => (
                            <button
                              key={subMenu.key}
                              onClick={() => {
                                setActiveDashboardSubMenu(subMenu.key);
                                setDashboardDropdownOpen(false);
                              }}
                              className={`block w-full text-left px-4 py-2 text-sm transition-colors duration-200 ${activeDashboardSubMenu === subMenu.key
                                ? 'bg-primary-50 text-primary-700'
                                : 'text-gray-700 hover:bg-gray-100'
                                }`}
                            >
                              {subMenu.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }
                if (tab.key === 'Management') {
                  return (
                    <div key={tab.key} className="relative">
                      <button
                        onClick={() => {
                          setActiveTab(tab.key);
                          setManagementDropdownOpen(!managementDropdownOpen);
                        }}
                        className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 flex items-center space-x-1 ${activeTab === tab.key
                          ? 'bg-primary-100 text-primary-700 border-b-2 border-primary-500'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                          }`}
                      >
                        <span>{tab.label}</span>
                        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${managementDropdownOpen ? 'rotate-180' : ''
                          }`} />
                      </button>
                      {managementDropdownOpen && activeTab === 'Management' && (
                        <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                          {managementSubMenus.map((subMenu) => (
                            <button
                              key={subMenu.key}
                              onClick={() => {
                                setActiveSubMenu(subMenu.key);
                                setManagementDropdownOpen(false);
                              }}
                              className={`block w-full text-left px-4 py-2 text-sm transition-colors duration-200 ${activeSubMenu === subMenu.key
                                ? 'bg-primary-50 text-primary-700'
                                : 'text-gray-700 hover:bg-gray-100'
                                }`}
                            >
                              {subMenu.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }
                if (tab.key === 'Report') {
                  return (
                    <div key={tab.key} className="relative">
                      <button
                        onClick={() => {
                          setActiveTab(tab.key);
                          setReportDropdownOpen(!reportDropdownOpen);
                        }}
                        className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 flex items-center space-x-1 ${activeTab === tab.key
                          ? 'bg-primary-100 text-primary-700 border-b-2 border-primary-500'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                          }`}
                      >
                        <span>{tab.label}</span>
                        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${reportDropdownOpen ? 'rotate-180' : ''
                          }`} />
                      </button>
                      {reportDropdownOpen && activeTab === 'Report' && (
                        <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                          {reportSubMenus.map((subMenu) => (
                            <button
                              key={subMenu.key}
                              onClick={() => {
                                setActiveReportSubMenu(subMenu.key);
                                setReportDropdownOpen(false);
                              }}
                              className={`block w-full text-left px-4 py-2 text-sm transition-colors duration-200 ${activeReportSubMenu === subMenu.key
                                ? 'bg-primary-50 text-primary-700'
                                : 'text-gray-700 hover:bg-gray-100'
                                }`}
                            >
                              {subMenu.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${activeTab === tab.key
                      ? 'bg-primary-100 text-primary-700 border-b-2 border-primary-500'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* User Profile and Actions */}
          <div className="hidden md:block">
            <div className="ml-4 flex items-center md:ml-6 space-x-4">
              {/* Search */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder={t('navigation.search')}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
              </div>

              {/* Language Switcher */}
              <div className="relative">
                <button
                  onClick={() => setLanguageDropdownOpen(!languageDropdownOpen)}
                  className="p-2 rounded-full text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <Languages className="h-5 w-5" />
                </button>
                {languageDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-32 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                    <button
                      onClick={() => changeLanguage('en')}
                      className={`block w-full text-left px-4 py-2 text-sm transition-colors duration-200 ${i18n.language === 'en'
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-700 hover:bg-gray-100'
                        }`}
                    >
                      {t('common.english')}
                    </button>
                    <button
                      onClick={() => changeLanguage('lo')}
                      className={`block w-full text-left px-4 py-2 text-sm transition-colors duration-200 ${i18n.language === 'lo'
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-700 hover:bg-gray-100'
                        }`}
                    >
                      {t('common.lao')}
                    </button>
                  </div>
                )}
              </div>

              {/* Notifications */}
              <button className="p-2 rounded-full text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                <Bell className="h-5 w-5" />
              </button>

              {/* User Profile */}
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2 bg-gray-50 rounded-lg px-3 py-2">
                  <div className="h-8 w-8 rounded-full bg-primary-500 flex items-center justify-center">
                    <User className="h-5 w-5 text-white" />
                  </div>
                  <div className="text-sm">
                    <div className="font-medium text-gray-900">{userName || t('navigation.user')}</div>
                    <div className="text-gray-500">{userRole === 'admin' ? t('common.admin') : t('navigation.user')}</div>
                  </div>
                </div>
                <button onClick={onLogout} className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-md">{t('common.logout')}</button>
              </div>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="bg-slate-50 p-2 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 transition-colors"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" aria-hidden="true" />
              ) : (
                <Menu className="h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white shadow-xl absolute w-full z-50">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 border-t border-gray-200 shadow-inner">
            {/* Mobile Profile Section */}
            <div className="flex items-center px-3 py-4 border-b border-gray-100 mb-2">
              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                <User className="h-6 w-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <div className="text-base font-medium text-slate-800">{userName || t('navigation.user')}</div>
                <div className="text-sm font-medium text-slate-500">{userRole === 'admin' ? t('common.admin') : t('navigation.user')}</div>
              </div>
              <button onClick={onLogout} className="ml-auto flex-shrink-0 p-2 text-sm text-red-600 hover:bg-red-50 rounded-md font-medium">
                {t('common.logout')}
              </button>
            </div>

            {tabs.map((tab) => {
              if (tab.key === 'Dashboard') {
                return (
                  <div key={tab.key}>
                    <button
                      onClick={() => {
                        setActiveTab(tab.key);
                        setDashboardDropdownOpen(!dashboardDropdownOpen);
                      }}
                      className={`flex items-center justify-between w-full px-3 py-3 rounded-md text-base font-medium ${activeTab === tab.key
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                        }`}
                    >
                      <span>{tab.label}</span>
                      <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${dashboardDropdownOpen && activeTab === 'Dashboard' ? 'rotate-180' : ''
                        }`} />
                    </button>
                    {dashboardDropdownOpen && activeTab === 'Dashboard' && (
                      <div className="ml-4 mt-1 space-y-1 border-l-2 border-blue-100 pl-2">
                        {dashboardSubMenus.map((subMenu) => (
                          <button
                            key={subMenu.key}
                            onClick={() => {
                              setActiveDashboardSubMenu(subMenu.key);
                              setMobileMenuOpen(false); // Close mobile menu after selection
                            }}
                            className={`block w-full text-left px-3 py-2.5 rounded-md text-sm font-medium ${activeDashboardSubMenu === subMenu.key
                              ? 'bg-blue-50 text-blue-700'
                              : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'
                              }`}
                          >
                            {subMenu.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              if (tab.key === 'Management') {
                return (
                  <div key={tab.key}>
                    <button
                      onClick={() => {
                        setActiveTab(tab.key);
                        setManagementDropdownOpen(!managementDropdownOpen);
                      }}
                      className={`flex items-center justify-between w-full px-3 py-3 rounded-md text-base font-medium ${activeTab === tab.key
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                        }`}
                    >
                      <span>{tab.label}</span>
                      <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${managementDropdownOpen && activeTab === 'Management' ? 'rotate-180' : ''
                        }`} />
                    </button>
                    {managementDropdownOpen && activeTab === 'Management' && (
                      <div className="ml-4 mt-1 space-y-1 border-l-2 border-blue-100 pl-2">
                        {managementSubMenus.map((subMenu) => (
                          <button
                            key={subMenu.key}
                            onClick={() => {
                              setActiveSubMenu(subMenu.key);
                              setMobileMenuOpen(false);
                            }}
                            className={`block w-full text-left px-3 py-2.5 rounded-md text-sm font-medium ${activeSubMenu === subMenu.key
                              ? 'bg-blue-50 text-blue-700'
                              : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'
                              }`}
                          >
                            {subMenu.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              if (tab.key === 'Report') {
                return (
                  <div key={tab.key}>
                    <button
                      onClick={() => {
                        setActiveTab(tab.key);
                        setReportDropdownOpen(!reportDropdownOpen);
                      }}
                      className={`flex items-center justify-between w-full px-3 py-3 rounded-md text-base font-medium ${activeTab === tab.key
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                        }`}
                    >
                      <span>{tab.label}</span>
                      <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${reportDropdownOpen && activeTab === 'Report' ? 'rotate-180' : ''
                        }`} />
                    </button>
                    {reportDropdownOpen && activeTab === 'Report' && (
                      <div className="ml-4 mt-1 space-y-1 border-l-2 border-blue-100 pl-2">
                        {reportSubMenus.map((subMenu) => (
                          <button
                            key={subMenu.key}
                            onClick={() => {
                              setActiveReportSubMenu(subMenu.key);
                              setMobileMenuOpen(false);
                            }}
                            className={`block w-full text-left px-3 py-2.5 rounded-md text-sm font-medium ${activeReportSubMenu === subMenu.key
                              ? 'bg-blue-50 text-blue-700'
                              : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'
                              }`}
                          >
                            {subMenu.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <button
                  key={tab.key}
                  onClick={() => {
                    setActiveTab(tab.key);
                    setMobileMenuOpen(false);
                  }}
                  className={`block px-3 py-3 rounded-md text-base font-medium w-full text-left ${activeTab === tab.key
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                    }`}
                >
                  {tab.label}
                </button>
              );
            })}

            {/* Language Switcher Mobile */}
            <div className="pt-4 pb-2 border-t border-gray-100 mt-4">
              <div className="px-3 text-sm font-medium text-slate-500 mb-2">{t('common.language')}</div>
              <div className="flex space-x-2 px-3">
                <button
                  onClick={() => changeLanguage('en')}
                  className={`px-4 py-2 text-sm font-medium rounded-md ${i18n.language === 'en' ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                >
                  English
                </button>
                <button
                  onClick={() => changeLanguage('lo')}
                  className={`px-4 py-2 text-sm font-medium rounded-md ${i18n.language === 'lo' ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                >
                  ລາວ
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;